import { useEffect, useState } from "react";
import { CheckCircle2, ChevronLeft, Clock, Paperclip, Play } from "lucide-react";
import { ResourceList } from "@/components/learn/ResourceList";
import { CertificateButton } from "@/components/learn/LearnerDashboard";

export const CourseView = ({ api, course, lessons, onBack, onOpen }) => {
  const [tab, setTab] = useState("lessons");
  const [resources, setResources] = useState(null);
  useEffect(() => { if (tab === "resources" && resources === null) api.get(`/learner/courses/${course.id}/materials`).then(({ data }) => setResources(data.materials || [])).catch(() => setResources([])); }, [tab, resources, api, course.id]);
  const done = lessons.filter((l) => l.completed).length;
  const pct = lessons.length ? Math.round((done / lessons.length) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="course-view">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="back-to-dashboard"><ChevronLeft size={12} /> My dashboard</button>
      <div className="rounded-2xl bg-[#06142e] p-6 text-white">
        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-sky-200">{course.award}</span>
        <h2 className="mt-2 font-heading text-2xl font-bold">{course.title}</h2>
        {course.summary && <p className="mt-1 text-sm text-slate-300">{course.summary}</p>}
        <div className="mt-4 flex items-center gap-4">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/15"><div className="h-full rounded-full bg-gradient-to-r from-sky-400 to-blue-500" style={{ width: `${pct}%` }} /></div>
          <span className="text-xs text-sky-100">{done}/{lessons.length} · {pct}%</span>
          <CertificateButton api={api} course={{ ...course, progress_pct: pct, lesson_count: lessons.length }} />
        </div>
      </div>
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-fit" role="tablist">
        {[["lessons", "Lessons"], ["resources", "Course materials"]].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)} className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid={`course-tab-${t}`}>{label}</button>
        ))}
      </div>
      {tab === "lessons" ? (
        <div className="space-y-2">
          {lessons.map((l, i) => (
            <button key={l.id} onClick={() => onOpen(l.id)} className="flex w-full items-center gap-3 rounded-xl border border-border bg-white p-4 text-left transition-colors hover:border-blue-300" data-testid={`lesson-${l.id}`}>
              {l.completed ? <CheckCircle2 size={28} className="shrink-0 text-green-600" /> : <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">{i + 1}</span>}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900">{l.title}</p>
                <p className="flex flex-wrap items-center gap-x-3 text-[11px] text-slate-500">
                  {l.video_questions ? <span className="font-medium text-blue-700">Video quiz · {l.video_questions} question{l.video_questions === 1 ? "" : "s"}</span> : <>
                  <span className="inline-flex items-center gap-1"><Clock size={10} /> {l.reading_minutes} min read</span>
                  <span>{l.has_quiz ? "Includes a quiz" : "Reading only"}</span></>}
                  {l.resource_count > 0 && <span className="inline-flex items-center gap-1"><Paperclip size={10} /> {l.resource_count} resource{l.resource_count === 1 ? "" : "s"}</span>}
                </p>
              </div>
              {l.best_score != null && !l.completed ? <span className="text-xs text-amber-600">{l.best_score}%</span> : <Play size={14} className="text-slate-400" />}
            </button>
          ))}
          {lessons.length === 0 && <p className="py-6 text-center text-sm italic text-slate-500">No lessons published yet.</p>}
        </div>
      ) : resources === null ? <p className="text-sm text-slate-500">Loading…</p> : <MaterialGroups resources={resources} />}
    </div>
  );
};

const MaterialGroups = ({ resources }) => {
  if (!resources.length) return <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm italic text-slate-500" data-testid="materials-empty">Your tutor hasn't added any course materials yet.</p>;
  const general = resources.filter((r) => !r.lesson_id);
  const byLesson = resources.filter((r) => r.lesson_id);
  return (
    <div className="space-y-5" data-testid="learner-materials">
      {general.length > 0 && <section><h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">For the whole course</h3><ResourceList resources={general} /></section>}
      {byLesson.length > 0 && <section><h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Lesson materials</h3><ResourceList resources={byLesson} showLesson /></section>}
    </div>
  );
};

export const LessonSidebar = ({ lessons, currentId, onOpen }) => (
  <nav className="space-y-1" data-testid="lesson-sidebar">
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Lessons</p>
    {lessons.map((l, i) => (
      <button key={l.id} onClick={() => onOpen(l.id)} className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition-colors ${l.id === currentId ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid={`sidebar-lesson-${l.id}`}>
        {l.completed ? <CheckCircle2 size={14} className={l.id === currentId ? "text-white" : "text-green-600"} /> : <span className={`flex h-3.5 w-3.5 items-center justify-center text-[9px] font-bold ${l.id === currentId ? "text-white" : "text-slate-400"}`}>{i + 1}</span>}
        <span className="truncate">{l.title}</span>
      </button>
    ))}
  </nav>
);
